import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, DatabaseBackup, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";

const LIMIT = 15;

export default function Reports() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [classification, setClassification] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT };
    if (q) params.q = q;
    if (from) params.from = from;
    if (to) params.to = to;
    if (classification) params.classification = classification;
    api
      .get("/observatory/reports", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, q, from, to, classification]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const exportUrl = (fmt) => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/observatory/export?format=${fmt}`;
    return base + (from ? `&from=${from}` : "") + (to ? `&to=${to}` : "");
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Archivo de informes</h1>
          <p className="text-sm text-slate-500 mt-1">{total} informe{total === 1 ? "" : "s"} archivado{total === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2">
          <a href={exportUrl("csv")} data-testid="export-csv-button" className="flex items-center gap-1.5 text-xs bg-[#12141d] border border-[#232838] hover:border-[#8b5cf6] rounded-md px-3 py-2 transition-colors">
            <Download className="w-3.5 h-3.5" /> CSV
          </a>
          <a href={exportUrl("json")} data-testid="export-json-button" className="flex items-center gap-1.5 text-xs bg-[#12141d] border border-[#232838] hover:border-[#8b5cf6] rounded-md px-3 py-2 transition-colors">
            <Download className="w-3.5 h-3.5" /> JSON
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`} data-testid="backup-button" className="flex items-center gap-1.5 text-xs bg-[#8b5cf6] hover:bg-[#7c3aed] rounded-md px-3 py-2 transition-colors font-medium">
            <DatabaseBackup className="w-3.5 h-3.5" /> Copia de seguridad
          </a>
        </div>
      </div>

      <div className="card-glow rounded-lg p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reports-filters">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar en texto y campos…"
            data-testid="filter-search-input"
            className="w-full bg-[#090a0f] border border-[#232838] rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors"
          />
        </div>
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="filter-from-input"
          className="bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors [color-scheme:dark]" />
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="filter-to-input"
          className="bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors [color-scheme:dark]" />
        <select value={classification} onChange={(e) => { setClassification(e.target.value); setPage(1); }} data-testid="filter-classification-select"
          className="bg-[#090a0f] border border-[#232838] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#8b5cf6] transition-colors">
          <option value="">Clasificación: todas</option>
          <option value="FACT">HECHO</option>
          <option value="INFERENCE">INFERENCIA</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="reports-loading"><Loader2 className="w-6 h-6 animate-spin text-[#8b5cf6]" /></div>
      ) : items.length === 0 ? (
        <div className="card-glow rounded-lg p-10 text-center" data-testid="reports-empty">
          <p className="text-sm text-slate-400">No hay informes que coincidan.</p>
          <p className="text-xs text-slate-600 mt-1">La base de datos se llena automáticamente desde Make — sin datos de ejemplo.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reports-list">
          {items.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/informes/${r.id}`)}
              data-testid={`report-row-${r.report_date}`}
              className="w-full card-glow rounded-lg p-4 text-left flex flex-wrap items-center gap-x-6 gap-y-2"
            >
              <div className="min-w-28">
                <div className="font-mono2 text-sm text-white">{r.report_date}</div>
                <div className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">{r.city || "—"}</div>
              </div>
              <div className="flex gap-4 text-xs text-slate-400 flex-1 flex-wrap">
                <span>{r.counts?.venues || 0} locales</span>
                <span>{r.counts?.events || 0} eventos</span>
                <span>{r.counts?.tools_channels || 0} herramientas</span>
                <span>{r.counts?.operational_problems || 0} problemas</span>
              </div>
              <div className="flex gap-2 items-center">
                {r.edited_manually && <span className="text-[10px] font-mono2 uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1a1d29] border border-[#3b4259] text-slate-400">Editado</span>}
                <span className="text-[10px] font-mono2 uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1a1d29] border border-[#232838] text-slate-500">
                  {r.extraction_method === "llm" ? "Auto-LLM" : r.extraction_method === "provided" ? "Estructurado" : "Solo texto"}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev"
            className="p-2 rounded-md bg-[#12141d] border border-[#232838] disabled:opacity-40 hover:border-[#8b5cf6] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono2 text-xs text-slate-500" data-testid="pagination-info">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="pagination-next"
            className="p-2 rounded-md bg-[#12141d] border border-[#232838] disabled:opacity-40 hover:border-[#8b5cf6] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
